import React from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Cell } from 'recharts';
import StatCard from './StatCard';

function CaseStudyLevel3({ data }) {
  if (!data) return null;

  const { counts, top_relieved, top_new_hotspot, delta_bins } = data;

  const totalOrigCongested = counts.relieved + counts.persistent;

  // Classification bar chart
  const classificationData = [
    { name: 'Relieved', value: counts.relieved, color: '#7CC644' },
    { name: 'Persistent', value: counts.persistent, color: '#f0ad4e' },
    { name: 'New Hotspot', value: counts.new_hotspot, color: '#ff6b6b' },
  ];

  // Delta V/C histogram
  const histogramData = delta_bins.map(bin => ({
    label: bin.label,
    count: bin.count,
    isNegative: bin.bin_start < 0,
  }));

  return (
    <div className="case-study-level">
      <h2>Level 3: Bottleneck Analysis</h2>

      <div className="stat-cards-row">
        <StatCard title="Relieved" value={counts.relieved} change={null} />
        <StatCard title="Persistent" value={counts.persistent} change={null} />
        <StatCard title="New Hotspots" value={counts.new_hotspot} change={null} />
        <StatCard title="Originally Congested" value={totalOrigCongested} change={null} />
      </div>

      <div className="charts-grid">
        <div className="chart-container">
          <h3>Bottleneck Classification</h3>
          <ResponsiveContainer width="100%" height={250}>
            <BarChart data={classificationData} margin={{ top: 5, right: 20, left: 20, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#3a3a3a" />
              <XAxis dataKey="name" stroke="#aaa" fontSize={12} />
              <YAxis stroke="#aaa" fontSize={11} />
              <Tooltip
                contentStyle={{ backgroundColor: '#2a2a2a', border: '1px solid #3a3a3a', color: '#fff' }}
              />
              <Bar dataKey="value" name="Count">
                {classificationData.map((entry, idx) => (
                  <Cell key={idx} fill={entry.color} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="chart-container">
          <h3>Delta V/C Distribution</h3>
          <ResponsiveContainer width="100%" height={250}>
            <BarChart data={histogramData} margin={{ top: 5, right: 20, left: 20, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#3a3a3a" />
              <XAxis dataKey="label" stroke="#aaa" fontSize={10} angle={-45} textAnchor="end" height={60} />
              <YAxis stroke="#aaa" fontSize={11} />
              <Tooltip
                contentStyle={{ backgroundColor: '#2a2a2a', border: '1px solid #3a3a3a', color: '#fff' }}
              />
              <Bar dataKey="count" name="Edge-Hours">
                {histogramData.map((entry, idx) => (
                  <Cell key={idx} fill={entry.isNegative ? '#7CC644' : '#ff6b6b'} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="data-table-container">
        <h3>Top 20 Relieved Bottlenecks</h3>
        <div className="table-scroll">
          <table className="case-study-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Edge ID</th>
                <th>Hour</th>
                <th>V/C (Baseline)</th>
                <th>V/C (Scenario)</th>
                <th>Delta V/C</th>
              </tr>
            </thead>
            <tbody>
              {top_relieved.map((item, idx) => (
                <tr key={idx}>
                  <td>{idx + 1}</td>
                  <td className="edge-id-cell">{item.edge_id}</td>
                  <td>{item.hour}</td>
                  <td>{item.plain_vc.toFixed(3)}</td>
                  <td>{item.cp_vc.toFixed(3)}</td>
                  <td style={{ color: '#7CC644' }}>{item.delta_vc.toFixed(3)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="data-table-container">
        <h3>Top 20 New Hotspots</h3>
        <div className="table-scroll">
          <table className="case-study-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Edge ID</th>
                <th>Hour</th>
                <th>V/C (Baseline)</th>
                <th>V/C (Scenario)</th>
                <th>Delta V/C</th>
              </tr>
            </thead>
            <tbody>
              {top_new_hotspot.map((item, idx) => (
                <tr key={idx}>
                  <td>{idx + 1}</td>
                  <td className="edge-id-cell">{item.edge_id}</td>
                  <td>{item.hour}</td>
                  <td>{item.plain_vc.toFixed(3)}</td>
                  <td>{item.cp_vc.toFixed(3)}</td>
                  <td style={{ color: '#ff6b6b' }}>+{item.delta_vc.toFixed(3)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

export default CaseStudyLevel3;
